import { createFileRoute, Link } from "@tanstack/react-router";
import { LegalLayout } from "@/components/legal/LegalLayout";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Política de Privacidad — Zyntra Tickets" },
      {
        name: "description",
        content:
          "Conocé cómo Zyntra Events trata tus datos personales conforme a la Ley N° 8968 de Costa Rica: finalidad, derechos ARCO, seguridad y contacto del responsable.",
      },
      { name: "robots", content: "index, follow" },
      { property: "og:title", content: "Política de Privacidad — Zyntra Tickets" },
      {
        property: "og:description",
        content:
          "Tratamiento de datos personales en la plataforma Zyntra Tickets: finalidad, base legal, derechos y contacto.",
      },
      { property: "og:type", content: "article" },
    ],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <LegalLayout
      title="Política de Privacidad"
      subtitle="Cómo recolectamos, usamos y protegemos tu información personal en Zyntra Tickets."
      lastUpdated="23 de abril de 2026"
    >
      <section>
        <h2>1. Responsable del tratamiento</h2>
        <p>
          El responsable del tratamiento de los datos personales recolectados a través
          de la plataforma <strong>Zyntra Tickets</strong> (en adelante, la
          “Plataforma”) es <strong>Zyntra Events S.R.L.</strong>, sociedad constituida
          conforme a las leyes de la República de Costa Rica, con domicilio en San
          José, Costa Rica.
        </p>
        <ul>
          <li>
            <strong>Razón social:</strong> Zyntra Events S.R.L.
          </li>
          <li>
            <strong>Correo de contacto legal:</strong>{" "}
            <a href="mailto:legal@zyntratickets.com">legal@zyntratickets.com</a>
          </li>
          <li>
            <strong>Sitio web:</strong>{" "}
            <a href="https://zyntratickets.com">https://zyntratickets.com</a>
          </li>
        </ul>
      </section>

      <section>
        <h2>2. Datos que recolectamos</h2>
        <p>
          Recolectamos únicamente los datos estrictamente necesarios para prestar el
          servicio y cumplir nuestras obligaciones legales y contractuales:
        </p>
        <h3>2.1 Datos personales identificativos</h3>
        <ul>
          <li>Nombre completo y nombre para mostrar.</li>
          <li>Correo electrónico.</li>
          <li>
            Imagen de avatar (cuando es proporcionada por vos o por tu proveedor de
            inicio de sesión).
          </li>
        </ul>
        <h3>2.2 Datos de autenticación</h3>
        <ul>
          <li>
            Credenciales de acceso (correo y contraseña, almacenada de forma cifrada
            mediante funciones de hash seguras).
          </li>
          <li>
            Datos provenientes de <strong>Google OAuth</strong> cuando elegís iniciar
            sesión con Google: nombre, correo electrónico verificado y avatar
            público. <em>No</em> recibimos tu contraseña de Google ni accedemos a tu
            agenda, contactos o documentos.
          </li>
        </ul>
        <h3>2.3 Datos financieros y de transacción</h3>
        <ul>
          <li>
            Historial de compras de entradas, ventas en reventa, movimientos de
            wallet y solicitudes de retiro (payouts).
          </li>
          <li>
            Datos bancarios o de pago necesarios para procesar payouts (IBAN, número
            de cuenta, número SINPE Móvil, banco, identificación). Estos datos se
            almacenan bajo controles estrictos de acceso.
          </li>
          <li>
            <strong>Importante:</strong> los datos completos de tarjeta de crédito o
            débito <strong>no son almacenados</strong> por Zyntra Events. Son
            procesados directamente por <strong>Stripe, Inc.</strong>, nuestro
            procesador de pagos certificado PCI-DSS Nivel 1.
          </li>
        </ul>
        <h3>2.4 Datos técnicos</h3>
        <ul>
          <li>Dirección IP y país aproximado.</li>
          <li>
            Tipo de navegador, sistema operativo y dispositivo (user agent).
          </li>
          <li>
            Cookies y tecnologías similares (ver nuestra{" "}
            <Link to="/cookies">Política de Cookies</Link>).
          </li>
          <li>Registros de actividad y validaciones de tickets.</li>
        </ul>
      </section>

      <section>
        <h2>3. Finalidad del tratamiento</h2>
        <p>Tratamos tus datos personales con las siguientes finalidades:</p>
        <ul>
          <li>Crear y administrar tu cuenta de usuario.</li>
          <li>Procesar la compra, validación, reventa y reembolso de entradas.</li>
          <li>Operar tu wallet digital y procesar payouts.</li>
          <li>
            Enviar comunicaciones transaccionales (confirmaciones, recordatorios de
            evento, recibos, alertas de seguridad).
          </li>
          <li>
            Cumplir con obligaciones legales, contables, fiscales y de prevención de
            fraude.
          </li>
          <li>Mejorar la seguridad, estabilidad y experiencia de la Plataforma.</li>
        </ul>
        <p>
          <strong>No vendemos, alquilamos ni cedemos tus datos personales</strong> a
          terceros con fines de marketing o publicidad.
        </p>
      </section>

      <section>
        <h2>4. Base legal</h2>
        <p>
          El tratamiento de datos personales se realiza conforme a la{" "}
          <strong>
            Ley N° 8968, Ley de Protección de la Persona frente al tratamiento de sus
            datos personales
          </strong>{" "}
          de la República de Costa Rica, y su reglamento, así como bajo los
          siguientes fundamentos:
        </p>
        <ul>
          <li>
            <strong>Consentimiento expreso:</strong> otorgado al crear tu cuenta y
            aceptar esta política y los Términos y Condiciones.
          </li>
          <li>
            <strong>Ejecución de un contrato:</strong> necesario para prestarte el
            servicio de venta de entradas, wallet y payouts.
          </li>
          <li>
            <strong>Cumplimiento de obligación legal:</strong> conservación de
            registros contables y fiscales.
          </li>
          <li>
            <strong>Interés legítimo:</strong> seguridad de la Plataforma y
            prevención de fraude.
          </li>
        </ul>
      </section>

      <section>
        <h2>5. Compartición de datos con terceros</h2>
        <p>
          Compartimos información con encargados de tratamiento únicamente en lo
          necesario para prestar el servicio:
        </p>
        <ul>
          <li>
            <strong>Stripe, Inc.</strong> (Estados Unidos) — procesamiento de pagos y
            verificación antifraude.
          </li>
          <li>
            <strong>Google LLC</strong> (Estados Unidos) — autenticación mediante
            Google OAuth, únicamente cuando elegís ese método.
          </li>
          <li>
            <strong>Proveedores de infraestructura cloud</strong> — alojamiento de
            la Plataforma, base de datos, almacenamiento y entrega de correos
            transaccionales.
          </li>
          <li>
            <strong>Autoridades competentes</strong> — cuando exista una orden
            judicial o requerimiento legal vinculante en Costa Rica.
          </li>
        </ul>
        <p>
          Todos nuestros encargados están obligados contractualmente a tratar los
          datos con niveles de seguridad equivalentes o superiores a los exigidos por
          la Ley N° 8968.
        </p>
      </section>

      <section>
        <h2>6. Transferencias internacionales</h2>
        <p>
          Algunos de nuestros encargados de tratamiento (como Stripe y Google) están
          ubicados fuera de Costa Rica, principalmente en los Estados Unidos y la
          Unión Europea. Estas transferencias se realizan bajo cláusulas
          contractuales y estándares de protección equivalentes a los exigidos por la
          legislación costarricense.
        </p>
      </section>

      <section>
        <h2>7. Derechos ARCO+</h2>
        <p>Como titular de los datos, tenés derecho a:</p>
        <ul>
          <li>
            <strong>Acceso:</strong> conocer qué datos tuyos tratamos.
          </li>
          <li>
            <strong>Rectificación:</strong> solicitar la corrección de datos
            inexactos o incompletos.
          </li>
          <li>
            <strong>Cancelación / supresión:</strong> solicitar la eliminación de tus
            datos cuando ya no sean necesarios.
          </li>
          <li>
            <strong>Oposición:</strong> oponerte a tratamientos específicos.
          </li>
          <li>
            <strong>Portabilidad:</strong> recibir una copia de tus datos en formato
            estructurado.
          </li>
          <li>
            <strong>Revocación del consentimiento:</strong> en cualquier momento, sin
            efectos retroactivos.
          </li>
        </ul>
      </section>

      <section>
        <h2>8. Procedimiento para ejercer tus derechos</h2>
        <p>
          Para ejercer cualquiera de los derechos anteriores, enviá una solicitud a{" "}
          <a href="mailto:legal@zyntratickets.com">legal@zyntratickets.com</a> desde
          el correo registrado en tu cuenta, indicando:
        </p>
        <ul>
          <li>Nombre completo.</li>
          <li>Derecho que querés ejercer.</li>
          <li>Detalle de la solicitud.</li>
          <li>Medio para recibir la respuesta.</li>
        </ul>
        <p>
          Responderemos en un plazo máximo de <strong>cinco (5) días hábiles</strong>{" "}
          desde la recepción de la solicitud, conforme al artículo 12 de la Ley N°
          8968.
        </p>
      </section>

      <section>
        <h2>9. Seguridad de los datos</h2>
        <p>Aplicamos medidas técnicas y organizativas, incluyendo:</p>
        <ul>
          <li>Cifrado en tránsito mediante TLS 1.2 o superior.</li>
          <li>Cifrado en reposo de bases de datos.</li>
          <li>Políticas estrictas de control de acceso (Row Level Security).</li>
          <li>Hash seguro de contraseñas.</li>
          <li>Registros de auditoría y monitoreo de actividad.</li>
          <li>Pruebas de seguridad periódicas.</li>
        </ul>
        <p>
          Aunque aplicamos los mejores estándares de la industria, ningún sistema es
          100 % infalible. En caso de incidente de seguridad que afecte tus datos,
          te notificaremos conforme a la legislación aplicable.
        </p>
      </section>

      <section>
        <h2>10. Retención de datos</h2>
        <ul>
          <li>
            <strong>Cuenta activa:</strong> mientras mantengas tu cuenta abierta.
          </li>
          <li>
            <strong>Registros financieros:</strong> mínimo cinco (5) años conforme a
            la normativa fiscal de Costa Rica.
          </li>
          <li>
            <strong>Cuenta cancelada:</strong> los datos no esenciales se anonimizan
            o eliminan dentro de los 90 días posteriores a la baja.
          </li>
        </ul>
      </section>

      <section>
        <h2>11. Cambios a esta política</h2>
        <p>
          Podemos actualizar esta política periódicamente. Notificaremos los cambios
          materiales mediante correo electrónico o aviso destacado en la Plataforma.
          La fecha de última actualización aparece al inicio de este documento.
        </p>
      </section>

      <section>
        <h2>12. Contacto</h2>
        <p>
          Para consultas sobre esta política o el tratamiento de tus datos:
        </p>
        <ul>
          <li>
            <strong>Correo:</strong>{" "}
            <a href="mailto:legal@zyntratickets.com">legal@zyntratickets.com</a>
          </li>
          <li>
            <strong>Responsable:</strong> Zyntra Events S.R.L., San José, Costa Rica.
          </li>
        </ul>
        <p>
          También podés consultar nuestros{" "}
          <Link to="/terms">Términos y Condiciones</Link> y nuestra{" "}
          <Link to="/cookies">Política de Cookies</Link>.
        </p>
      </section>
    </LegalLayout>
  );
}
